import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';

// Material UI
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';

// Icons
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

// Components
import TextField from '../../../../components/inputs/input';
import Table from '../../../../components/table';
import EditDetailModal from '../editDetailModal';
import ChipState from '../../../../components/ChipState';
import { stockControlStatesFormat } from '../../../../models/StockControlEntity';
import { stockControllUtils } from '../../../../utils/stockControll-utils';

const columns = [{
    id: 'refId',
    label: 'SKU'
},
{
    id: 'name',
    label: 'Descripcion'
},
{
    id: 'colorCode',
    label: 'Color'
},
{
    id: 'quantity',
    label: 'Stock Real',
    align: 'center',
    sort: true
},
{
    id: 'expectedQuantity',
    label: 'Stock Esperado',
    align: 'center',
    sort: true
},
{
    id: 'differenceToShow',
    label: 'Diferencia',
    align: 'center',
    sort: true
},
{
    id: 'observation',
    label: 'Observaciones'
}]

const StockControlDetailsTable = ({ details, onAdd, onRemove, onChange, onAddMassive, onSetMassive, state }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [refId, setRefId] = useState('');
    const [openDetail, setOpenDetail] = useState(false);
    const [detailSelected, setDetailSelected] = useState(null);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleAddProduct = () => {
        onAdd(refId);
        setRefId('');
    }

    const handleRemoveProduct = (refId) => {
        onRemove(refId);
        setRefId('');
    }

    const onLoadFile = async (e, callback) => {
        e.preventDefault();

        const file = e.target.files;
        const reader = new FileReader();
        reader.onloadend = async (e) => {
            const content = (e.target.result);
            const productsArray = stockControllUtils.parseContent(content);
            callback(productsArray);
        };

        reader.readAsText(file[0]);

        e.target.value = null;
    };

    const handleInputChange = (event) => {
        setRefId(stockControllUtils.formatRefId(event.target.value))
    }

    return (
        <Paper>
            {
                openDetail &&
                <EditDetailModal
                    editQuantity
                    detail={detailSelected}
                    open={openDetail}
                    onClose={() => setOpenDetail(false)}
                    onSave={onChange}
                />
            }
            <Grid container direction='column'>
                <Grid
                    container
                    spacing={1}
                    alignItems='center'
                    justifyContent='space-between'
                    style={{ padding: '8px 16px' }}
                >
                    <Grid container direction='column' size={4}>
                        <Typography
                            component='h2'
                            color='primary'
                            variant='h5'
                        >
                            Detalles
                        </Typography>
                        {
                            state &&
                            <Grid size={3}>
                                <ChipState state={state} label={stockControlStatesFormat[state]} />
                            </Grid>
                        }
                    </Grid>
                    <Grid
                        container
                        spacing={1}
                        justifyContent='flex-end'
                        alignItems='center'
                        size={8}
                    >
                        <Grid
                            size={{
                                xs: 4,
                                lg: 5
                            }}
                        >
                            <TextField
                                fullWidth
                                name='refId'
                                label='SKU o Módulo'
                                onChange={handleInputChange}
                                value={refId}
                                size='small'
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter')
                                        handleAddProduct()
                                }}
                            />
                        </Grid>
                        <Grid size={2}>
                            <Button
                                fullWidth
                                color='primary'
                                variant='contained'
                                onClick={handleAddProduct}
                                disabled={!refId}
                            >
                                Agregar
                            </Button>
                        </Grid>
                        <Grid
                            size={{
                                xs: 4,
                                lg: 3
                            }}
                        >
                            <div className="upload-btn-wrapper">
                                <Button
                                    fullWidth
                                    variant="contained"
                                    component="label"
                                    color='primary'
                                >
                                    Cargar Archivo
                                    <input
                                        type="file"
                                        hidden
                                        onChange={(e) => onLoadFile(e, onAddMassive)}
                                    />
                                </Button>
                            </div>
                        </Grid>
                        <Grid size={2}>
                            <div className="upload-btn-wrapper">
                                <Button
                                    fullWidth
                                    variant="contained"
                                    component="label"
                                    color='primary'
                                >
                                    Recuento
                                    <input
                                        type="file"
                                        hidden
                                        onChange={(e) => onLoadFile(e, onSetMassive)}
                                    />
                                </Button>
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid>
                    <Divider />
                    <Table
                        rows={details}
                        columns={columns}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        actions={(onChange || onRemove)
                            ? (item) =>
                                <>
                                    {
                                        onChange &&
                                        <IconButton
                                            size='small'
                                            color='primary'
                                            onClick={() => {
                                                setOpenDetail(true);
                                                setDetailSelected(item);
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    }
                                    {
                                        onRemove &&
                                        <IconButton
                                            size='small'
                                            color='primary'
                                            onClick={() => handleRemoveProduct(item.refId)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    }
                                </>
                            : null
                        }
                    />
                    <TablePagination
                        count={details.length}
                        rowsPerPageOptions={[10, 25, 100]}
                        component="div"
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Grid>
            </Grid>
        </Paper>
    );
}

StockControlDetailsTable.defaultProps = {
    details: [],
    onAdd: () => { },
    onRemove: () => { },
    onChange: () => { }
}

StockControlDetailsTable.prototype = {
    details: PropTypes.arrayOf(PropTypes.object),
    onAdd: PropTypes.func,
    onRemove: PropTypes.func,
    onChange: PropTypes.func
}

export default StockControlDetailsTable;